import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { editWatchReducer } from './editWatchReducer';
import { EditWatchContext, editWatchInitialState, TTypeOfModalWindow } from './editWatchContext';
import { EditWatchActionTypes } from './editWatchActions';
import { IGetWatchInfo, IServerError, ISerialNumberExistsValidationError, IUpdateWatch } from 'types';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getWatchInfo } from 'backend/watches';

export const EditWatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editWatchReducer, editWatchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const watchID = params.get('watchID');

	const { response, loading, refetch } = useFetchData<IGetWatchInfo, string>(
		watchID ? getWatchInfo : null,
		watchID || '',
	);

	useNamedHeader(response?.data.serialNumber);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditWatchActionTypes.SET_ALL_DATA,
				payload: {
					watchID: watchID || '',
					serialNumber: response.data.serialNumber,
					IDSelectedCustomer: response.data.customerId,
					IDSelectedRestaurant: response.data.restaurantId,
					isActive: response.data.isActive,
				},
			});
		}
	}, [watchID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditWatchActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditWatchActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditWatchActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const changeSerialNumber = useCallback((value: string) => {
		dispatch({
			type: EditWatchActionTypes.CHANGE_SERIAL_NUMBER,
			payload: {
				serialNumber: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditWatchActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditWatchActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateWatch>) => {
		dispatch({
			type: EditWatchActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditWatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: Nullable<IServerError<ISerialNumberExistsValidationError>>) => {
		dispatch({
			type: EditWatchActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	return (
		<EditWatchContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				changeSerialNumber,
				setIDCustomer,
				setIDRestaurant,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				needRefetchData: state.needRefetchData,
				isLoading: state.isLoading,
				watchID: state.watchID,
				serialNumber: state.serialNumber,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				isActive: state.isActive,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditWatchContext.Provider>
	);
};
